import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Phone } from "lucide-react";
import { CITIES, SOCIAL } from "../data/siteData";
import { trackCTA } from "../lib/analytics";
import { makeoverWhatsAppUrl } from "../data/makeoverData";

/**
 * Floating WhatsApp FAB. On mobile it sits above the sticky bottom CTA bar
 * (bar is 64px + safe-area; we add another 16px gap so the FAB never overlaps
 * the bar, the Book My Tutor card, footer content or branch addresses).
 * On xl+ desktops (where the sticky bar is hidden) it sits comfortably at the
 * bottom-right corner.
 */
export default function WhatsAppButton() {
    const [open, setOpen] = useState(false);

    const cityList = Object.values(CITIES);
    const generalText = encodeURIComponent(
        "Hi Anand Tutorials, I would like to know more about home / online tuition. Please contact me."
    );

    // Mobile bottom offsets — arbitrary Tailwind values keep responsive
    // xl: overrides working. 64 (bar) + 16 (gap) = 80px above the CTA bar,
    // and the popup panel sits ~88px above the FAB.
    const fabBottom = "bottom-[calc(80px+env(safe-area-inset-bottom,0px))] xl:bottom-6";
    const panelBottom = "bottom-[calc(168px+env(safe-area-inset-bottom,0px))] xl:bottom-24";
    // Also inset from the right by max(1rem, safe-area-inset-right) so the FAB
    // is never hidden by a landscape notch / curved-edge camera cutout.
    const rightInset = "right-[max(1rem,env(safe-area-inset-right,0px))] sm:right-[max(1.5rem,env(safe-area-inset-right,0px))]";

    return (
        <>
            {open && (
                <div
                    className={`fixed ${panelBottom} ${rightInset} z-[60] w-72 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-up`}
                    data-testid="whatsapp-panel"
                >
                    <div className="bg-[#25D366] p-4 text-white relative">
                        <button onClick={() => setOpen(false)} className="absolute top-3 right-3 p-1 hover:bg-white/15 rounded" aria-label="Close">
                            <X className="w-4 h-4" />
                        </button>
                        <div className="font-heading font-semibold text-base">WhatsApp Anand Tutorials</div>
                        <div className="text-xs opacity-90 mt-0.5">Pick a branch — we reply within minutes.</div>
                    </div>
                    <div className="p-2">
                        {/* V179 — Fixed menu order per product spec:
                            1. 1:1 Online Classes
                            2. Jabalpur
                            3. Bhopal
                            4. Nagpur
                            5. 1:1 Makeover Classes
                            6. Tutor — WhatsApp only (bottom, visually separated)
                            Numbers, greetings, labels, icons unchanged. */}

                        {/* 1 — 1:1 Online Classes (Pan India + Worldwide default) */}
                        <a
                            href={`https://wa.me/917000530316?text=${generalText}`}
                            onClick={() => trackCTA("whatsapp_clicked", { cta_location: "floating-whatsapp-panel", whatsapp_city: "online-1-1" })}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="whatsapp-online-classes"
                            className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                            <div>
                                <div className="font-semibold text-sm text-[#0F172A]">1:1 Online Classes</div>
                                <div className="text-xs text-slate-500">Pan India &amp; Worldwide</div>
                            </div>
                            <MessageCircle className="w-4 h-4 text-[#25D366]" />
                        </a>

                        {/* 2-4 — City branches in strict Jabalpur → Bhopal → Nagpur order */}
                        {["jabalpur", "bhopal", "nagpur"].map((slug) => {
                            const c = cityList.find((x) => x.slug === slug);
                            if (!c) return null;
                            return (
                                <a
                                    key={c.slug}
                                    href={`https://wa.me/${c.phoneRaw || SOCIAL.whatsapp}?text=${generalText}`}
                                    onClick={() => trackCTA("whatsapp_clicked", { cta_location: "floating-whatsapp-panel", whatsapp_city: c.slug })}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`whatsapp-${c.slug}`}
                                    className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                    <div>
                                        <div className="font-semibold text-sm text-[#0F172A]">{c.name}</div>
                                        <div className="text-xs text-slate-500">Home Tuition &amp; Tutoring</div>
                                    </div>
                                    <MessageCircle className="w-4 h-4 text-[#25D366]" />
                                </a>
                            );
                        })}

                        {/* 5 — 1:1 Makeover Classes (Online + Offline program) */}
                        <a
                            href={makeoverWhatsAppUrl("918085796268")}
                            onClick={() => trackCTA("whatsapp_clicked", { cta_location: "floating-whatsapp-panel", whatsapp_city: "makeover" })}
                            target="_blank"
                            rel="noreferrer"
                            data-smart-contact-skip="true"
                            data-testid="whatsapp-makeover"
                            className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                            <div>
                                <div className="font-semibold text-sm text-[#0F172A]">1:1 Makeover Classes</div>
                                <div className="text-xs text-slate-500">Online + Offline program</div>
                            </div>
                            <MessageCircle className="w-4 h-4 text-[#25D366]" />
                        </a>

                        {/* 6 — Tutor WhatsApp-only line (visually separated at bottom) */}
                        <a
                            href="https://wa.me/917610702222?text=Hi%20Anand%20Tutorials%2C%20I%20am%20a%20tutor%20and%20would%20like%20to%20connect."
                            onClick={() => trackCTA("whatsapp_clicked", { cta_location: "floating-whatsapp-panel", whatsapp_city: "tutor" })}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="whatsapp-tutor"
                            className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-fuchsia-50 transition-colors border-t border-slate-100 mt-1"
                        >
                            <div>
                                <div className="font-semibold text-sm text-[#0F172A]">Tutor — WhatsApp only</div>
                                <div className="text-xs text-slate-500">Dedicated tutor channel</div>
                            </div>
                            <MessageCircle className="w-4 h-4 text-fuchsia-600" />
                        </a>
                        <Link
                            to="/contact"
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 px-3 py-2.5 mt-1 border-t border-slate-100 text-sm text-[#1E3A8A] font-semibold hover:bg-blue-50 rounded-lg"
                            data-testid="whatsapp-or-contact"
                        >
                            <Phone className="w-4 h-4" />
                            Or fill the contact form
                        </Link>
                    </div>
                </div>
            )}
            <button
                onClick={() => setOpen(!open)}
                aria-label="WhatsApp Chat"
                data-testid="whatsapp-fab"
                className={`fixed ${fabBottom} ${rightInset} z-[60] w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white shadow-2xl flex items-center justify-center group transition-transform hover:scale-105`}
            >
                {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-7 h-7" />}
                {!open && (
                    <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25 pointer-events-none"></span>
                )}
            </button>
        </>
    );
}
